import { MagnifyingGlass } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { fetchVenueAvailabilityStatuses } from 'apis/restaurant-api/merchant-admin/venue/api';
import { SearchResult } from 'apis/restaurant-api/merchant-admin/venue-search/types';
import { useThemeMode } from 'common/themeMode';
import { Typography } from 'common/typography';
import {
  VENUE_STATUS_TITLE,
  VENUE_STATUS_TOOLTIPS,
  getVenueAvailabilityStatus,
} from 'modules/venue-availability-status/helpers';

import { AvailabilityStatus } from './AvailabilityStatus';
import {
  SelectorContainer,
  InformationContainer,
  AvatarTagSearchContainer,
  AvatarContainer,
  EntityAvatar,
  PopoverContent,
  PopoverTitle,
  LabelContainer,
  EntityTitle,
  AddressAndTagContainer,
  TagContainer,
  SearchContainer,
  StatusLightBackground,
  IconContainer,
  AvatarTagContainer,
} from './styles';

type SearchItemSelectorButtonV2 = {
  nothingSelected: boolean;
  activeResult?: SearchResult;
  onClick: () => void;
  isOpen?: boolean;
  isCollapsed?: boolean;
  isDaasVenueSelected?: boolean;
  isPOS?: boolean;
};

type ActiveResultGeneric = {
  label: string;
  city: string;
  address: string;
  listImage: string | undefined;
};

const REFETCH_VENUE_AVAILABILITY_STATUS_INTERVAL_MS = 5 * 60 * 1000; // 5 minute

const SearchItemSelectorButtonV2 = ({
  activeResult,
  nothingSelected,
  onClick,
  isCollapsed,
  isDaasVenueSelected,
  isPOS,
}: SearchItemSelectorButtonV2) => {
  const intl = useIntl();
  const { themeMode } = useThemeMode();

  const venueId = activeResult?.type === 'venue' ? activeResult.value.id : null;
  const { data: venuesData } = useQuery(
    ['/v2/merchant-admin/venue_availability_statuses', venueId],
    () => fetchVenueAvailabilityStatuses({ venueIds: venueId ? [venueId] : [] }, intl.locale),
    {
      enabled: venueId !== null,
      refetchInterval: REFETCH_VENUE_AVAILABILITY_STATUS_INTERVAL_MS,
    },
  );

  const { label, city, address, listImage } = useMemo<ActiveResultGeneric>(() => {
    if (activeResult?.type === 'venue') {
      const { name: venueName, city: venueCity, address: venueAddress, listimage: venueListImage } = activeResult.value;
      return {
        listImage: venueListImage || undefined,
        address: !isCollapsed ? venueAddress : '',
        label: venueName[0].value,
        city: venueCity,
      };
    }
    if (activeResult?.type === 'merchant') {
      const { name: merchantName, country } = activeResult.value;
      return {
        listImage: '',
        address: country.toString(),
        label: merchantName[0].value,
        city: '',
      };
    }
    if (activeResult?.type === 'enterprise' || activeResult?.type === 'brand') {
      const { name, country } = activeResult.value;
      return {
        listImage: '',
        address: country ? country.toString() : '',
        label: name,
        city: '',
      };
    }

    return {
      listImage: '',
      address: '',
      label: '',
      city: '',
    };
  }, [activeResult, isCollapsed]);

  const initials = label
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('');

  const theme = useTheme();

  const [currentVenue] = venuesData || [];
  const venueAvailabilityStatus = currentVenue ? getVenueAvailabilityStatus(currentVenue) : null;
  return (
    <SelectorContainer onClick={onClick}>
      {nothingSelected && <FormattedMessage id="searchItemSelector.selectVenue" />}
      {!nothingSelected && (
        <InformationContainer>
          <AvatarTagSearchContainer isCollapsed={isCollapsed}>
            <AvatarTagContainer isCollapsed={isCollapsed}>
              <AvatarContainer>
                <EntityAvatar size="small" imgSrc={listImage} initials={initials} />
                {venueAvailabilityStatus && (
                  <LegacyPopover
                    content={
                      <PopoverContent>
                        <PopoverTitle>
                          <FormattedMessage id={VENUE_STATUS_TITLE[venueAvailabilityStatus]} />
                        </PopoverTitle>
                        <Typography.Body3>
                          <FormattedMessage id={VENUE_STATUS_TOOLTIPS[venueAvailabilityStatus]} />
                        </Typography.Body3>
                      </PopoverContent>
                    }
                  >
                    <StatusLightBackground>
                      <AvailabilityStatus status={venueAvailabilityStatus} />
                    </StatusLightBackground>
                  </LegacyPopover>
                )}
              </AvatarContainer>
              {!isCollapsed && (
                <TagContainer dir={theme.cbDirection}>
                  {isPOS && (
                    <Tag variant="outlineBlack" size="tiny" uppercase>
                      <FormattedMessage id="merchant-integrations.pos-integrated" />
                    </Tag>
                  )}
                  {isDaasVenueSelected && (
                    <Tag variant="outlineBlack" size="tiny" uppercase>
                      <FormattedMessage id="searchItemSelector.daas" />
                    </Tag>
                  )}
                </TagContainer>
              )}
            </AvatarTagContainer>
            {!isCollapsed && (
              <SearchContainer>
                <IconContainer>
                  <MagnifyingGlass
                    fill={themeMode === 'light' ? colors.pepper64 : colors.salt72}
                    width={16}
                    height={16}
                  />
                </IconContainer>
              </SearchContainer>
            )}
          </AvatarTagSearchContainer>
          {!isCollapsed && (
            <LegacyPopover
              content={
                <PopoverContent>
                  <Typography.Body3>{label}</Typography.Body3>
                </PopoverContent>
              }
              placement="top"
              // We only show the popover if the label is longer than 36 characters, decided here:
              // https://wolt.slack.com/archives/C05L46YPRKQ/p1692010148385649?thread_ts=1691995596.855469&cid=C05L46YPRKQ
              showOnHover={label.length > 36}
            >
              <LabelContainer dir={theme.cbDirection}>
                <EntityTitle>{label}</EntityTitle>
                <AddressAndTagContainer>
                  {address && (
                    <Typography.Small>
                      {address}
                      {city && ', '}
                    </Typography.Small>
                  )}
                  {city && <Typography.Small>{city}</Typography.Small>}
                </AddressAndTagContainer>
              </LabelContainer>
            </LegacyPopover>
          )}
        </InformationContainer>
      )}
    </SelectorContainer>
  );
};

export default SearchItemSelectorButtonV2;
